import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Table } from '@quintoandar-tokko/jopijs';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import parentStyles from '../dashboardTables.module.scss';
import specificStyles from './doneVisitsTable.module.scss';
import LoadingMoreSkeleton from '../components/LoadingMoreSkeleton';
import { getPerformanceDoneVisitsTable } from '@/store/slices/components/dashboard/service';
import buildQuery from '../../utils/buildQuery';
import useObserver from '@/hooks/useObserver';
import { setPerformanceModals } from '@/store/slices/components/dashboard/DashboardSlice';
import HeaderCell from '../components/HeaderCell';
import buildTableQuery from '../../utils/buildTableQuery';
import { TABLE_SORT_FIELDS } from '../../constants';
import DoneVisitsTableRow from './DoneVisitsTableRow';
import useTrackPerformanceTable from '@/components/Dashboard/customHooks/useTrackDashboard';
import { TRACK_ELEMENT } from '@/components/Dashboard/constants/track';

export default function DoneVisitsTable() {
  const dispatch = useDispatch();
  const { selectedIds } = useSelector((state) => state.FilterBarSlice.filters);
  const { doneVisits } = useSelector(
    (state) => state.DashboardSlice.performanceModals
  );
  const { ref: loadMoreRef, isInViewport } = useObserver();
  const track = useTrackPerformanceTable();

  const headers = [
    {
      id: 1,
      title: Trans(translations, 'Property'),
      subtitle: Trans(translations, 'Location'),
      field: TABLE_SORT_FIELDS.PROPERTY_ADDRESS,
    },
    {
      id: 2,
      title: Trans(translations, 'VisitType'),
      field: TABLE_SORT_FIELDS.VISIT_TYPE,
    },
    {
      id: 3,
      title: Trans(translations, 'Contact'),
      field: TABLE_SORT_FIELDS.CONTACT_NAME,
    },
    {
      id: 4,
      title: Trans(translations, 'Agent'),
      field: TABLE_SORT_FIELDS.AGENT_NAME,
    },
    {
      id: 5,
      title: Trans(translations, 'VisitQualification'),
      field: TABLE_SORT_FIELDS.QUALIFICATION,
    },
    {
      id: 6,
      title: Trans(translations, 'VisitDate'),
      field: TABLE_SORT_FIELDS.WHEN,
    },
  ];

  useEffect(() => {
    if (!doneVisits.has_next) return;
    if (!isInViewport || doneVisits.loading) return;

    const query = buildQuery(selectedIds);
    const tableQuery = buildTableQuery(doneVisits, {
      withNextPage: true,
    });

    dispatch(getPerformanceDoneVisitsTable({ query, tableQuery }));
  }, [isInViewport]);

  const handleCellClick = (header) => {
    dispatch(
      setPerformanceModals({
        key: 'doneVisits',
        subkey: 'sortBy',
        value: header.field,
      })
    );
    track.doneVisits(TRACK_ELEMENT.COLUMN_ORDER);
  };

  return (
    <Table
      id="done-visits-table"
      className={`${parentStyles.table} ${specificStyles.doneVisitsTableWithQualification}`}
    >
      <Table.Header className={parentStyles.tableHeader}>
        {headers.map((header) => (
          <HeaderCell
            key={header.id}
            id={header.id}
            title={header.title}
            subtitle={header.subtitle}
            field={header.field}
            sortBy={doneVisits.sortBy}
            sortDirection={doneVisits.sortDirection}
            onClick={() => handleCellClick(header)}
          />
        ))}
      </Table.Header>

      <Table.Rows>
        {doneVisits.list.map((doneVisit) => (
          <DoneVisitsTableRow
            key={doneVisit.id}
            doneVisit={doneVisit}
            showQualificationColumn
          />
        ))}

        <Box as="tr" ref={loadMoreRef} style={{ height: 1, opacity: 0 }} />

        {(doneVisits.loading || doneVisits.has_next) && (
          <LoadingMoreSkeleton columns={headers.length} />
        )}
      </Table.Rows>
    </Table>
  );
}
